import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combine class names + dedupe Tailwind conflicts.
 * Standard utility used by every component.
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as a localized integer with thousands separators.
 * 1234 -> "1,234"
 */
export function formatNumber(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US').format(n);
}

/**
 * Format a USD price. 450 cents -> "$4.50"
 */
export function formatUSD(cents) {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/**
 * Format a number compactly. 1234 -> "1.2K", 1500000 -> "1.5M"
 */
export function formatCompact(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

/**
 * Format an ISO date as a short relative string ("2 hours ago")
 * or fallback to a short date for older timestamps.
 */
export function formatRelative(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  const now = Date.now();
  const seconds = Math.floor((now - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format an ISO date as a short absolute date ("Jun 25, 2026").
 */
export function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Copy text to clipboard. Returns true on success.
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Mask an API key for safe display.
 * ***... -> sk_aBcD********
 */
export function maskKey(prefix, length = 4) {
  return `${prefix}${'*'.repeat(Math.max(length, 8))}`;
}

/**
 * Play a subtle success chime using the Web Audio API.
 * No audio file needed — synthesized in-browser.
 * Safe to call repeatedly (only plays once even if called rapidly).
 *
 * Two-tone pleasant "ding": 880 Hz → 1320 Hz, 0.15s each.
 */
let _lastChimeAt = 0;
export function playSuccessChime() {
  const now = Date.now();
  // Debounce — don't play more than once every 2 seconds
  if (now - _lastChimeAt < 2000) return;
  _lastChimeAt = now;

  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    function tone(freq, start, dur) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    }
    tone(880, 0, 0.15);
    tone(1320, 0.12, 0.18);
    // Close context after the last note
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch (e) {
    // Silent failure — some browsers block audio without user gesture
  }
}

/**
 * Detect if a browser supports the Notification API + can show
 * desktop notifications. Returns true if we should try.
 */
export function canShowNotifications() {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission === 'granted'
  );
}

/**
 * Show a desktop notification (if permitted). Returns true if shown.
 */
export function showNotification(title, body) {
  if (!canShowNotifications()) return false;
  try {
    new Notification(title, { body, icon: '/logo.png' });
    return true;
  } catch {
    return false;
  }
}

/**
 * Request notification permission. Returns the new permission state.
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'denied';
  }
}